// Parser de errores de PocketBase.
//
// PB devuelve ClientResponseError con shape:
//   { status, message: "Failed to create record.", response: { data: { email: { code, message } } } }
//
// El message top-level es genérico ("Failed to create record"). Los detalles
// reales viven en response.data.<field>.message. Este helper extrae lo útil.

interface PbFieldError {
  code?: string;
  message?: string;
}

interface PbErrorShape {
  message?: string;
  status?: number;
  response?: { data?: Record<string, PbFieldError> };
  data?: Record<string, PbFieldError>;
}

/**
 * Devuelve un mensaje legible a partir de un error de PB.
 * Si el error tiene field-level errors (ej. email: unique), los lista.
 * Si no, devuelve el message top-level o un fallback.
 */
export function formatPbError(err: unknown, fallback = 'Error desconocido'): string {
  if (!err) return fallback;
  if (typeof err === 'string') return err;
  if (!(typeof err === 'object')) return String(err);

  const e = err as PbErrorShape;
  const fieldErrors =
    (e.response && e.response.data) ||
    (e.data && typeof e.data === 'object' ? e.data : null);

  if (fieldErrors && typeof fieldErrors === 'object') {
    const parts: string[] = [];
    for (const [field, info] of Object.entries(fieldErrors)) {
      if (info && typeof info === 'object' && 'message' in info && info.message) {
        parts.push(`${humanizeField(field)}: ${info.message}`);
      }
    }
    if (parts.length > 0) return parts.join(' · ');
  }

  return e.message || (err instanceof Error ? err.message : fallback);
}

function humanizeField(field: string): string {
  const map: Record<string, string> = {
    email: 'Email',
    password: 'Contraseña',
    passwordConfirm: 'Confirmación de contraseña',
    name: 'Nombre',
    slug: 'Slug',
    domain: 'Dominio',
    role: 'Rol',
    tenant: 'Tenant',
    cms_repo_url: 'URL del repo',
    cms_repo_branch: 'Branch',
    cms_github_token_env: 'Env var del PAT',
    cms_github_pat: 'PAT de GitHub',
  };
  return map[field] || field;
}
